import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Chip,
  Dialog,
  Divider,
  Grid,
  Image,
  ProductPrice,
  Stack,
  Typography,
} from '@packages/shared';
import { FormattedMessage, useIntl } from 'react-intl';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import { AplusFragmentFragmentDoc } from '@packages/gql/generated/shopping/AplusFragmentFragmentDoc';
import { ToggleServiceProductDocument } from '@packages/gql/generated/shopping/ToggleServiceProductDocument';
import { ArrowMiniDown32, Checkmark32 } from '@packages/themes/icons';
import { ServiceLayer } from '@packages/cms-components/src/modules/ServiceLayer/ServiceLayer';
import { AplusServiceCountTextField } from './AplusServiceCountTextField';
import { useEventTracking } from '../Tracking/useEventTracking';

/* GraphQL */ `
  fragment AplusFragment on BasketAPlus {
    clusters {
      label
      services {
        serviceType
        serviceIcon
        uuid
        name
        price {
          value
          currency
        }
        included
        layerLink
        selected
        serviceCount {
          current
          maximum
        }
      }
    }
  }
`;

type AplusProps = {
  aplusData: FragmentType<typeof AplusFragmentFragmentDoc>;
  lineItemId: string;
  quantity: number;
};

/* GraphQL */ `
  mutation ToggleServiceProduct(
    $locale: String!
    $lineItemId: String!
    $serviceUuid: String!
    $count: Int
  ) {
    toggleServiceProduct(
      locale: $locale
      lineItemId: $lineItemId
      serviceUuid: $serviceUuid
      count: $count
    ) {
      ...BasketFragment
    }
  }
`;

export const Aplus = ({ aplusData, lineItemId, quantity }: AplusProps) => {
  const { locale } = useIntl();
  const fragmentData = getFragmentData(AplusFragmentFragmentDoc, aplusData);
  const { clusters } = fragmentData || {};
  const [expanded, setExpanded] = useState<boolean>(true);
  const [activeServiceDialog, setActiveServiceDialog] = useState<
    { uuid: string; name: string } | undefined
  >(undefined);
  const [state, toggleServiceProduct] = useAuthMutation(ToggleServiceProductDocument);
  const dispatch = useEventTracking();

  const onClickHandler = (serviceUuid: string, count?: number) => {
    if (!state.fetching) {
      toggleServiceProduct({ locale, lineItemId, serviceUuid, count });
    }
  };

  let timeout: NodeJS.Timeout;
  const handleCountChange = (serviceUuid: string, count: number) => {
    // use a timeout to prevent too many backend calls
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      toggleServiceProduct({ locale, lineItemId, serviceUuid, count });
    }, 1500);
  };

  return (
    <Accordion
      sx={{
        border: '0 none',
        justifyContent: 'flex-start',
      }}
      disableGutters
      variant="outlined"
      expanded={expanded}
      onChange={(_, newValue) => setExpanded(newValue)}
    >
      <AccordionSummary
        sx={{
          justifyContent: 'flex-start',
          gap: '0.625rem',
          '.MuiAccordionSummary-content': { flexGrow: 0 },
          padding: '0rem 1rem 0rem',
        }}
        expandIcon={<ArrowMiniDown32 sx={{ color: 'text.dark' }} />}
      >
        <Typography variant="body2" component="div">
          {!expanded && (
            <Chip
              sx={{
                mr: 2,
                borderColor: 'success.main',
                borderWidth: '1px',
                borderStyle: 'solid',
                backgroundColor: 'success.light',
                color: 'success.contrastText',
              }}
              size="small"
              label={<FormattedMessage id="Aplus.hint" defaultMessage="TIPP" />}
            />
          )}
          <FormattedMessage id="Aplus.order" defaultMessage="Services jetzt dazu bestellen" />
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {clusters.map((cluster, key) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={`${lineItemId}-cluster-${key}`}>
            <Divider />
            <Typography
              variant="body3"
              component="p"
              fontWeight="fontWeightSemiBold"
              p="0.75rem 0 0.375rem"
            >
              {cluster?.label}
            </Typography>
            {cluster?.services.map((service) => (
              <Grid
                key={service.uuid}
                container
                sx={{
                  p: { xs: '0.625rem 0 0.5rem', md: '0.438rem 0 0.5rem' },
                  mt: { xs: 1, md: '0' },
                }}
              >
                <Grid item container justifyContent="flex-start" direction="row" flexWrap="nowrap">
                  <Grid item xs={1} md={0.5}>
                    {service.included ? (
                      <Checkmark32
                        data-testid="aplus-checkmark"
                        sx={{ color: 'success.main', fontSize: '1.5rem', mr: '0.625rem' }}
                      />
                    ) : (
                      <Checkbox
                        data-testid="aplus-checkbox"
                        sx={{
                          p: '0',
                          ml: '0.15rem',
                          mt: '0.25rem',
                          mr: '1.15rem',
                          alignItems: 'flex-start',
                          borderRadius: '0',
                          svg: {
                            fontSize: '1.25rem',
                          },
                        }}
                        checked={!!service.selected}
                        onClick={() => onClickHandler(service.uuid)}
                      />
                    )}
                  </Grid>
                  <Grid item xs={1} md={0.65}>
                    {quantity &&
                      quantity > 1 &&
                      service.selected &&
                      service.serviceCount === null && (
                        <Chip
                          data-testid="aplus-quantity"
                          size="small"
                          label={quantity}
                          sx={{ mt: '0.15rem', mr: 1 }}
                        />
                      )}
                  </Grid>
                  <Grid item container direction="column" flexGrow={1} rowGap={1}>
                    <Box display="flex" flexDirection="row">
                      <Stack mr={1} justifyContent="flex-start">
                        <Image
                          width="28"
                          height="28"
                          unoptimized
                          src={service.serviceIcon}
                          alt={service.name || ''}
                        />
                      </Stack>
                      <Stack mr="1.16rem">
                        <Typography variant="body2">{service.name || ''}</Typography>
                        <Typography
                          variant="body3"
                          component="p"
                          color="text.secondary"
                          onClick={() => {
                            dispatch('aplus', 'viewLayer');
                            setActiveServiceDialog({ uuid: service.uuid, name: service.name! });
                          }}
                          sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          <FormattedMessage id="Aplus.moreinfo" defaultMessage="Weitere Infos" />
                        </Typography>
                        {service.selected && service.serviceCount?.current && (
                          <AplusServiceCountTextField
                            count={service.serviceCount.current}
                            maximum={service.serviceCount.maximum}
                            onChange={(value) => handleCountChange(service.uuid, value)}
                          />
                        )}
                      </Stack>
                    </Box>
                  </Grid>
                  <Stack alignItems="flex-end" flex="1 0 auto">
                    <Typography variant="body2" component="h4" fontWeight="fontWeightSemiBold">
                      {service.included ? (
                        <Typography variant="body2" sx={{ color: 'success.main' }}>
                          <FormattedMessage id="Aplus.included" defaultMessage="inklusive" />
                        </Typography>
                      ) : (
                        <Typography variant="body2" component="div">
                          <ProductPrice
                            price={service.price!.value}
                            currency={service.price!.currency}
                            fontVariantPrice="h6"
                          />
                        </Typography>
                      )}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            ))}
          </div>
        ))}
        {activeServiceDialog && (
          <Dialog
            open
            handleClose={() => setActiveServiceDialog(undefined)}
            title={activeServiceDialog.name || ''}
            dialogContent={<ServiceLayer serviceLayerId={`a-plus_${activeServiceDialog.uuid}`} />}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};
